<template>
    <v-container fluid class="nexus-container">
      <!-- Header -->
      <v-row>
        <v-col cols="12">
          <h1 class="nexus-title">PROJECT MANAGEMENT</h1>
        </v-col>
      </v-row>
  
      <!-- Statistics Dashboard -->
      <v-row class="mb-4">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card">
            <v-card-text>
              <div class="stat-label">Active Projects</div>
              <div class="stat-value">{{ activeProjects.length }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card">
            <v-card-text>
              <div class="stat-label">In Progress</div>
              <div class="stat-value">{{ getStatusCount('In Progress') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card">
            <v-card-text>
              <div class="stat-label">Completed This Month</div>
              <div class="stat-value">{{ completedThisMonth }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card">
            <v-card-text>
              <div class="stat-label">Pending</div>
              <div class="stat-value">{{ getStatusCount('Pending') }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
  
      <!-- Action Bar -->
      <v-row class="mb-4">
        <v-col cols="12" md="8">
          <div class="filters">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="All Status"
              outlined
              dense
              class="nexus-input"
              style="max-width: 200px;"
            ></v-select>
          </div>
        </v-col>
        <v-col cols="12" md="4" class="text-right">
          <v-btn class="nexus-btn" @click="openAddDialog">
            <v-icon left>mdi-plus</v-icon>
            Add New Project
          </v-btn>
        </v-col>
      </v-row>
  
      <!-- Active Projects Table -->
      <v-card class="nexus-card mb-6">
        <div class="section-header">
          <h2 class="section-title">ACTIVE PROJECTS</h2>
          <div class="section-divider"></div>
          <span class="project-count">{{ filteredActiveProjects.length }}</span>
        </div>
        
        <v-data-table
          :headers="projectHeaders"
          :items="filteredActiveProjects"
          :items-per-page="10"
          class="nexus-table"
          @click:row="openEditDialog"
        >
          <template v-slot:item.projectNumber="{ item }">
            <span class="project-number">{{ item.projectNumber }}</span>
          </template>
  
          <template v-slot:item.status="{ item }">
            <span :class="getStatusClass(item.status)">{{ item.status }}</span>
          </template>
  
          <template v-slot:item.complete="{ item }">
            <v-checkbox
              v-model="item.complete"
              @click.stop
              @change="markChange(item, 'complete', !item.complete, item.complete)"
              :class="{ 'row-edited': isRowEdited(item) }"
            ></v-checkbox>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- Save Changes Section -->
      <v-row class="save-section">
        <v-col cols="12" class="text-center">
          <v-chip
            v-if="pendingChanges.length > 0"
            color="error"
            text-color="white"
            class="changes-badge mr-4"
          >
            {{ pendingChanges.length }} Unsaved Change{{ pendingChanges.length > 1 ? 's' : '' }}
          </v-chip>
          <v-btn
            class="save-btn"
            :disabled="pendingChanges.length === 0"
            @click="saveAllChanges"
            x-large
          >
            <v-icon left>mdi-content-save</v-icon>
            Save All Changes
          </v-btn>
        </v-col>
      </v-row>
  
      <!-- Completed Projects Table -->
      <v-card class="nexus-card">
        <div class="section-header">
          <h2 class="section-title">COMPLETED PROJECTS</h2>
          <div class="section-divider"></div>
          <span class="project-count">{{ completedProjects.length }}</span>
        </div>
        
        <v-data-table
          :headers="projectHeaders"
          :items="completedProjects"
          :items-per-page="10"
          class="nexus-table"
          @click:row="openEditDialog"
        >
          <template v-slot:item.projectNumber="{ item }">
            <span class="project-number">{{ item.projectNumber }}</span>
          </template>
  
          <template v-slot:item.status="{ item }">
            <span :class="getStatusClass(item.status)">{{ item.status }}</span>
          </template>
  
          <template v-slot:item.complete="{ item }">
            <v-checkbox
              v-model="item.complete"
              @click.stop
              @change="markChange(item, 'complete', !item.complete, item.complete)"
            ></v-checkbox>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- Add/Edit Project Dialog -->
      <v-dialog v-model="dialog" max-width="900px" class="nexus-dialog">
        <v-card>
          <v-card-title class="nexus-dialog-title">
            <span class="text-h5">{{ dialogTitle }}</span>
          </v-card-title>
  
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.projectNumber"
                    label="Project #"
                    disabled
                    outlined
                    dense
                    class="nexus-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.date"
                    label="Date"
                    type="date"
                    outlined
                    dense
                    class="nexus-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.description"
                    label="Description"
                    outlined
                    dense
                    class="nexus-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.author"
                    label="Author"
                    disabled
                    outlined
                    dense
                    class="nexus-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.salesman"
                    label="Salesman"
                    outlined
                    dense
                    class="nexus-input"
                    placeholder="Enter salesman name"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedItem.qty"
                    label="Quantity"
                    type="number"
                    outlined
                    dense
                    class="nexus-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedItem.material"
                    label="Material"
                    outlined
                    dense
                    class="nexus-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="editedItem.size"
                    label="Size"
                    outlined
                    dense
                    class="nexus-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.nexusDwgNumber"
                    label="Nexus Dwg Number"
                    outlined
                    dense
                    class="nexus-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editedItem.price"
                    label="Price"
                    outlined
                    dense
                    class="nexus-input"
                    prefix="$"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="editedItem.status"
                    :items="statusOptions"
                    label="Status"
                    outlined
                    dense
                    class="nexus-input"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-file-input
                    v-model="projectFiles"
                    multiple
                    accept=".pdf"
                    label="Upload Project PDFs"
                    outlined
                    dense
                    class="nexus-input"
                    prepend-icon="mdi-file-pdf-box"
                    :loading="isUploading"
                    :disabled="isUploading"
                  >
                    <template v-slot:selection="{ text }">
                      <v-chip small label color="red darken-2" text-color="white">
                        {{ text }}
                      </v-chip>
                    </template>
                  </v-file-input>
                  
                  <div v-if="uploadStatusMessage" 
                      :class="{'green--text': uploadStatusMessage.includes('Success'), 'error--text': uploadStatusMessage.includes('failed')}"
                      class="text-caption mt-1 ml-8">
                    {{ uploadStatusMessage }}
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="editedItem.remarks"
                    label="Remarks"
                    outlined
                    rows="3"
                    class="nexus-input"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
  
          <v-card-actions class="px-6 pb-6">
            <v-spacer></v-spacer>
            <v-btn class="nexus-btn-outline" @click="closeDialog">Cancel</v-btn>
            <v-btn class="nexus-btn" @click="saveProject">Save Project</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Loading Overlay -->
      <v-overlay :value="loading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </v-container>
  </template>
  
  <script>
  import { firebaseApp } from '@/firebaseConfig';
import { getFirestore } from 'firebase/firestore';
import { storage } from '@/firebaseConfig'; 
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const db = getFirestore(firebaseApp);
  import { 
    collection, 
    doc, 
    getDocs, 
    addDoc, 
    updateDoc, 
    runTransaction,
    query,
    where,
    orderBy,
    onSnapshot
  } from 'firebase/firestore';
  import { format } from 'date-fns';
  
  export default {
    name: 'SharedServices',
    
    data() {
      return {
        loading: false,
        dialog: false,
        editMode: false,
        statusFilter: 'All Status',
        
        projects: [],
        pendingChanges: [],
        editedRows: new Set(),
        projectFiles: [],
        uploadStatusMessage: 'Files Uploaded Successfully.', // To show "success files uploaded"
        isUploading: false,
        
        projectHeaders: [
          { text: 'Project #', value: 'projectNumber', sortable: true },
          { text: 'Description', value: 'description' },
          { text: 'Author', value: 'author' },
          { text: 'Salesman', value: 'salesman' },
          { text: 'Date', value: 'date' },
          { text: 'Qty', value: 'qty' },
          { text: 'Material', value: 'material' },
          { text: 'Size', value: 'size' },
          { text: 'Status', value: 'status' },
          { text: 'Complete', value: 'complete', sortable: false }
        ],
        
        statusOptions: [
          'All Status',
          'Pending',
          'In Progress',
          'Waiting',
          'On Hold',
          'Complete',
          'Cancelled'
        ],
        
        editedItem: {
          projectNumber: '',
          description: '',
          author: '',
          salesman: '',
          date: '',
          qty: 0,
          material: '',
          size: '',
          remarks: '',
          nexusDwgNumber: '',
          price: '',
          complete: false,
          status: 'Pending'
        },
        
        defaultItem: {
          projectNumber: '',
          description: '',
          author: '',
          salesman: '',
          date: new Date().toISOString().substr(0, 10),
          qty: 0,
          material: '',
          size: '',
          remarks: '',
          nexusDwgNumber: '',
          price: '',
          complete: false,
          status: 'Pending'
        }
      };
    },
    
    computed: {
      activeProjects() {
        return this.projects.filter(p => !p.complete);
      },
      
      completedProjects() {
        return this.projects.filter(p => p.complete);
      },
      
      filteredActiveProjects() {
        if (this.statusFilter === 'All Status') {
          return this.activeProjects;
        }
        return this.activeProjects.filter(p => p.status === this.statusFilter);
      },
      
      completedThisMonth() {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        
        return this.completedProjects.filter(p => {
          const projectDate = new Date(p.date);
          return projectDate.getMonth() === currentMonth && 
                 projectDate.getFullYear() === currentYear;
        }).length;
      },
      
      dialogTitle() {
        return this.editMode ? 'Edit Project' : 'Add New Project';
      },
      
      currentUser() {
        // Get from your auth system
        return this.$store?.state?.user?.displayName || 'Current User';
      }
    },
    
    created() {
      this.loadProjects();
    },
    
    beforeUnmount() {
      if (this.unsubscribe) {
        this.unsubscribe();
      }
    },
    
    methods: {
      async uploadProjectFiles(projectNumber) {
        if (!this.projectFiles || this.projectFiles.length === 0) return [];
      
        this.isUploading = true;
        this.uploadStatusMessage = 'Uploading PDFs...';
        const uploadedFilesData = [];

          try {
          // Upload all files concurrently using Promise.all for speed
            const uploadPromises = this.projectFiles.map(async (file) => {
            // Create a file path: projects/202604-01/my-spec.pdf
            const fileRef = ref(storage, `projects/${projectNumber}/${file.name}`);
            
            await uploadBytes(fileRef, file);
            const downloadURL = await getDownloadURL(fileRef);
            
            return {
              name: file.name,
              url: downloadURL,
              uploadedAt: new Date().toISOString()
            };
          });

          // Wait for all uploads to finish
          uploadedFilesData.push(...await Promise.all(uploadPromises));
        
          this.uploadStatusMessage = `Success! ${uploadedFilesData.length} files uploaded.`;
          return uploadedFilesData;

        } catch (error) {
          console.error("Upload failed:", error);
          this.uploadStatusMessage = 'Upload failed. Please try again.';
          throw error;
        } finally {
          this.isUploading = false;
        }
      },
      async loadProjects() {
        this.loading = true;
        try {
          // Set up real-time listener
          const projectsRef = collection(db, 'projects');
          
          this.unsubscribe = onSnapshot(projectsRef, (snapshot) => {
            this.projects = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
          });
        } catch (error) {
          console.error('Error loading projects:', error);
          this.$toast.error('Failed to load projects');
        } finally {
          this.loading = false;
        }
      },
      
      async getNextProjectNumber(projectDate) {
        const yearMonth = format(new Date(projectDate), 'yyyyMM');
        const counterRef = doc(db, 'projectCounters', yearMonth);
        
        try {
          const newCount = await runTransaction(db, async (transaction) => {
            const counterDoc = await transaction.get(counterRef);
            const currentCount = counterDoc.exists() ? counterDoc.data().count : 0;
            const nextCount = currentCount + 1;
            transaction.set(counterRef, { count: nextCount }, { merge: true });
            return nextCount;
          });
          
          // ✅ CHANGE: Prepend 'ZA-' to the project number
          return `ZA-${yearMonth}-${String(newCount).padStart(2, '0')}`;
        } catch (error) {
          console.error('Error generating project number:', error);
          throw error;
        }
      },
      
      openAddDialog() {
        this.editMode = false;
        this.editedItem = { ...this.defaultItem };
        this.editedItem.author = this.currentUser;
        this.dialog = true;
      },
      
      openEditDialog(item) {
        this.editMode = true;
        this.editedItem = { ...item };
        this.dialog = true;
      },
      
      closeDialog() {
        this.dialog = false;
        this.editedItem = { ...this.defaultItem };
      },
      
      async saveProject() {
        try {
          if (this.editMode) {
            // Track changes for existing project
            const originalProject = this.projects.find(p => p.id === this.editedItem.id);
            if (originalProject) {
              Object.keys(this.editedItem).forEach(key => {
                if (this.editedItem[key] !== originalProject[key] && key !== 'id') {
                  this.markChange(
                    this.editedItem,
                    key,
                    originalProject[key],
                    this.editedItem[key]
                  );
                }
              });
            }
            
            // Update local state
            const index = this.projects.findIndex(p => p.id === this.editedItem.id);
            if (index !== -1) {
              this.projects[index] = { ...this.editedItem };
            }
          } else {
            // It's a new project. Generate the number first.
            const projectNumber = await this.getNextProjectNumber(this.editedItem.date);
            this.editedItem.projectNumber = projectNumber;
            
            // UPLOAD FILES FIRST before saving the project
            let fileLinks = [];
            if (this.projectFiles && this.projectFiles.length > 0) {
              fileLinks = await this.uploadProjectFiles(projectNumber);
            }
            
            // Attach the file data to the project object
            this.editedItem.files = fileLinks;
            
            this.editedItem.createdAt = new Date().toISOString();
            this.editedItem.createdBy = this.currentUser;
            // ✅ CHANGE: Add origin flag to mark this as ZA_APP origin
            this.editedItem.origin = 'ZA_APP';

            this.pendingChanges.push({
              type: 'create',
              data: { ...this.editedItem }
            });
            
            // Add to local state
            this.projects.push({ ...this.editedItem, id: 'temp-' + Date.now() });
          }
          
          this.closeDialog();
          this.$toast.info('Changes staged. Click "Save All Changes" to commit.');
        } catch (error) {
          console.error('Error saving project:', error);
          this.$toast.error('Failed to save project');
        }
      },
      
      markChange(item, field, oldValue, newValue) {
        this.pendingChanges.push({
          type: 'update',
          projectId: item.id,
          projectNumber: item.projectNumber,
          field: field,
          oldValue: oldValue,
          newValue: newValue,
          timestamp: new Date().toISOString(),
          user: this.currentUser
        });
        
        this.editedRows.add(item.id);
      },
      
      isRowEdited(item) {
        return this.editedRows.has(item.id);
      },
      
      async saveAllChanges() {
        if (this.pendingChanges.length === 0) return;
        
        this.loading = true;
        try {
          const batch = [];
          
          for (const change of this.pendingChanges) {
            if (change.type === 'create') {
              // Add new project
              const projectRef = await addDoc(collection(db, 'projects'), change.data);
              
              // Log to change log
              await addDoc(collection(db, 'changeLogs'), {
                timestamp: new Date().toISOString(),
                userId: this.currentUser,
                projectNumber: change.data.projectNumber,
                field: 'created',
                oldValue: null,
                newValue: 'Project created',
                action: 'create'
              });
            } else if (change.type === 'update') {
              // Update existing project
              const projectRef = doc(db, 'projects', change.projectId);
              await updateDoc(projectRef, {
                [change.field]: change.newValue,
                updatedAt: new Date().toISOString(),
                updatedBy: this.currentUser
              });
              
              // Log to change log
              await addDoc(collection(db, 'changeLogs'), {
                timestamp: change.timestamp,
                userId: this.currentUser,
                projectNumber: change.projectNumber,
                field: change.field,
                oldValue: change.oldValue,
                newValue: change.newValue,
                action: 'update'
              });
            }
          }
          
          this.$toast.success(`Saved ${this.pendingChanges.length} change(s) successfully!`);
          
          // Clear pending changes
          this.pendingChanges = [];
          this.editedRows.clear();
          
        } catch (error) {
          console.error('Error saving changes:', error);
          this.$toast.error('Failed to save changes');
        } finally {
          this.loading = false;
        }
      },
      
      getStatusCount(status) {
        return this.activeProjects.filter(p => p.status === status).length;
      },
      
      getStatusClass(status) {
        const statusMap = {
          'Pending': 'status-badge status-pending',
          'In Progress': 'status-badge status-progress',
          'Waiting': 'status-badge status-waiting',
          'On Hold': 'status-badge status-hold',
          'Complete': 'status-badge status-complete',
          'Cancelled': 'status-badge status-cancelled'
        };
        return statusMap[status] || 'status-badge';
      }
    }
  };
  </script>
  
  <style scoped>
  @import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap");
  
  .nexus-container {
    background: linear-gradient(135deg, #475569 0%, #334155 100%);
    min-height: 100vh;
    font-family: 'Inter', sans-serif;
  }
  
  .nexus-title {
    color: white;
    font-weight: 700;
    font-size: 2.5rem;
    text-align: center;
    letter-spacing: 2px;
    margin: 0 0 30px 0;
  }
  
  .nexus-card {
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(10px);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 
      0 20px 40px -12px rgba(0, 0, 0, 0.3),
      0 0 0 1px rgba(255, 255, 255, 0.1);
    margin-top: 20px;
    transition: all 0.3s ease;
  }
  
  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding: 24px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid #e2e8f0;
  }
  
  .section-title {
    color: #475569;
    font-weight: 600;
    font-size: 1.25rem;
    letter-spacing: 1px;
    margin: 0;
    white-space: nowrap;
  }
  
  .section-divider {
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #475569 0%, rgba(71, 85, 105, 0.2) 100%);
    margin-left: 20px;
    border-radius: 1px;
  }
  
  .project-count {
    background: #475569;
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
    margin-left: 12px;
  }
  
  .nexus-input >>> .v-input__control {
    border-radius: 12px;
  }
  
  .nexus-input >>> .v-input__slot {
    background: rgba(248, 250, 252, 0.8) !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 12px !important;
    transition: all 0.3s ease;
  }
  
  .nexus-input >>> .v-input__slot:hover {
    border-color: #475569 !important;
    background: rgba(255, 255, 255, 0.9) !important;
  }
  
  .nexus-input >>> .v-input--is-focused .v-input__slot {
    border-color: #475569 !important;
    background: rgba(255, 255, 255, 1) !important;
    box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
  }
  
  .nexus-btn {
    background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
    color: white !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
    text-transform: none !important;
    box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
    transition: all 0.3s ease !important;
  }
  
  .nexus-btn:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.4) !important;
  }
  
  .nexus-btn-outline {
    border: 2px solid #475569 !important;
    color: #475569 !important;
    background: transparent !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
    text-transform: none !important;
    transition: all 0.3s ease !important;
  }
  
  .nexus-btn-outline:hover {
    background: #475569 !important;
    color: white !important;
    transform: translateY(-2px) !important;
  }
  
  .nexus-dialog >>> .v-card {
    border-radius: 20px !important;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px) !important;
  }
  
  .nexus-dialog-title {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    color: #1e293b !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
    border-bottom: 1px solid #e2e8f0 !important;
  }
  
  .nexus-table {
    background: rgba(255, 255, 255, 0.95) !important;
    border-radius: 12px !important;
    overflow: hidden !important;
  }
  
  .nexus-table >>> .v-data-table__wrapper {
    border-radius: 12px !important;
  }
  
  .nexus-table >>> th {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    color: #475569 !important;
    font-weight: 600 !important;
    border-bottom: 1px solid #e2e8f0 !important;
  }
  
  .nexus-table >>> tbody tr:hover {
    background: rgba(71, 85, 105, 0.05) !important;
    cursor: pointer;
  }
  
  .project-number {
    font-weight: 600;
    color: #475569;
    font-family: 'Courier New', monospace;
  }
  
  .status-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
    display: inline-block;
    text-align: center;
    min-width: 100px;
  }
  
  .status-pending { background: #e5e7eb; color: #6b7280; }
  .status-progress { background: #dbeafe; color: #1e40af; }
  .status-waiting { background: #fed7aa; color: #c2410c; }
  .status-hold { background: #e9d5ff; color: #6b21a8; }
  .status-complete { background: #d1fae5; color: #065f46; }
  .status-cancelled { background: #fee2e2; color: #b91c1c; }
  
  .stat-card {
    background: rgba(255, 255, 255, 0.98);
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
  }
  
  .stat-label {
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 8px;
  }
  
  .stat-value {
    color: #1e293b;
    font-size: 2rem;
    font-weight: 700;
  }
  
  .save-section {
    padding: 30px 0;
    text-align: center;
  }
  
  .save-btn {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
    color: white !important;
    border-radius: 12px !important;
    padding: 16px 48px !important;
    font-weight: 600 !important;
    font-size: 1.1rem !important;
    letter-spacing: 0.5px !important;
    box-shadow: 0 4px 14px 0 rgba(16, 185, 129, 0.4) !important;
    transition: all 0.3s ease !important;
  }
  
  .save-btn:hover:not(:disabled) {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 25px 0 rgba(16, 185, 129, 0.5) !important;
  }
  
  .save-btn:disabled {
    background: #94a3b8 !important;
    cursor: not-allowed !important;
    box-shadow: none !important;
  }
  
  .changes-badge {
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  
  .row-edited {
    background: rgba(251, 191, 36, 0.1) !important;
    border-left: 3px solid #f59e0b !important;
  }
  
  .filters {
    display: flex;
    gap: 10px;
  }
  
  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #475569 0%, #334155 100%);
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
  }
  
  /* Responsive Design */
  @media (max-width: 768px) {
    .nexus-title {
      font-size: 1.75rem;
      letter-spacing: 1px;
    }
    
    .section-title {
      font-size: 1.1rem;
    }
    
    .nexus-card {
      margin: 10px;
      border-radius: 16px;
    }
  }
  </style>